/**
 * Report-Definition für die Meetings-Liste.
 *
 * Übersetzt den geladenen Meetings-Datenstand in ein {@link ReportDocument}.
 * Analog zu {@link ./projektauftragReport}: deklariert nur den Inhalt, der
 * gemeinsame Renderer erzeugt das PDF.
 */
import {
  buildDocument,
  buildSection,
  tableBlock,
  textBlock,
  type ReportBlock,
  type ReportDocument,
} from '../../../lib/pdf/reportModel';
import { richTextToPlainText } from '../../../lib/richText';

export interface MeetingsReportItem {
  titel: string | null;
  typ: string | null;
  datum: string;
  teilnehmer: string | null;
  /** Protokoll/Notizen als Rich-Text-HTML (aus {@link PmRichTextEditor}); optional. */
  notes?: string | null;
}

export interface MeetingsReportInput {
  items: MeetingsReportItem[];
}

/** 'YYYY-MM-DD' -> 'DD.MM.YYYY'; leere/ungültige Werte -> ''. */
function formatDate(iso: string | null): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

export function buildMeetingsReport(input: MeetingsReportInput): ReportDocument {
  const block: ReportBlock =
    input.items.length > 0
      ? tableBlock(
          [
            { header: 'Titel', weight: 2.4 },
            { header: 'Typ', weight: 1.4 },
            { header: 'Datum', weight: 1.2 },
            { header: 'Teilnehmer', weight: 2.4 },
          ],
          input.items.map((m) => [
            m.titel || '—',
            m.typ || '—',
            formatDate(m.datum) || '—',
            m.teilnehmer || '—',
          ]),
        )!
      : { kind: 'note', value: 'Noch keine Meetings erfasst.' };

  // Ein Protokoll-Abschnitt je Meeting mit Notizen (leere übersprungen), gebündelt
  // unter einem gemeinsamen Titel "Protokolle" — je ein Text-Block pro Meeting.
  const protokollBlocks = input.items
    .map((m) => textBlock(
      [m.titel || 'Meeting', formatDate(m.datum)].filter(Boolean).join(' — '),
      richTextToPlainText(m.notes),
    ))
    .filter((b): b is ReportBlock => b != null);

  return buildDocument([
    { title: 'Meetings', blocks: [block] },
    buildSection('Protokolle', protokollBlocks),
  ]);
}
